// NPM packages
import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useTheme, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import styled from '@emotion/styled';

// All other imports
import { useServices } from 'services';
import useResourceGuard from 'hooks/useResourceGuard';
import PageContent from 'components/core/PageContent';
import AdditionalResource from './AdditionalResource';
import FeaturedResource from './FeaturedResource';
import DataProducts from './DataProducts';
import Statistics from './Statistics';
import JoinButton from 'components/project/JoinButton';
import Project from 'types/Project';
import Link from 'components/core/Link';

const bsecSettings = {
    msdliveProjectSearchKey: 'BSEC UIFL',
    msdliveProjectStatsKey: 'msdlive-project-bsec',
    msdliveProjectSlug: 'bsec',
    informationalLinks: [
        {
            name: 'BSEC Homepage',
            description: 'Follow for news and updates from the BSEC team!',
            url: 'https://bsec.21cc.jhu.edu/',
        },
        {
            name: 'CoURAGE',
            description: 'Coast-Urban-Rural Atmospheric Gradient Experiment',
            url: 'https://www.arm.gov/research/campaigns/amf2024courage',
        },
    ],
    featuredResources: [
        {
            url: 'https://data.msdlive.org/records/6yawb-zyx60/latest',
            title: 'BSEC Weather Station Data',
            summary: 'Measurements of temperature, humidity, rainfall, wind, and sunlight across Baltimore city.',
            img: '/bsec/weather_station.jpg',
        },
        {
            url: 'https://data.msdlive.org/records/x1x98-dxr27/latest',
            title: 'BSEC Air Quality Data',
            summary: 'Measurements of the concentrations and attenuations of various aerosols in ten minute averages.',
            img: '/bsec/air_quality.jpg',
        },
    ]
};

const Root = styled(PageContent)({
    paddingTop: '1rem!important',
});

const Flexy = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    minWidth: '24rem',
    flex: '1',
    alignItems: 'flex-start',
    '@media (width >= 48rem)': {
        alignItems: 'flex-end',
    }
});

export interface BSECLandingProps {
    className?: string;
  }

function BSECLanding(props: BSECLandingProps): React.ReactElement | null {

    const { className } = props;

    const theme = useTheme();
    
    const { projectService } = useServices();

    const location = useLocation();

    useEffect(() => {
        if (projectService && location) {
            const slug = location?.pathname?.split('/')?.[2];
            if (slug) {
                projectService.selectProject(slug);
            }
        }
    }, [projectService, location])

    const guardContent = useResourceGuard({
        resource: projectService.selectedProject,
    });

    let body: React.ReactNode;

    if (guardContent) {
        body = guardContent;
    } else {

        const project = projectService.selectedProject as Project;
        console.log(project)

        body = (
            <div
                style={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1rem',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: '2rem',
                        marginBottom: '1rem',
                    }}
                >
                    <img
                        alt="BSEC"
                        style={{
                            height: '9rem',
                            maxWidth: '12rem',
                            objectFit: 'contain',
                        }}
                        src={project.links.logo}
                    />
                    <Typography variant='h2'>
                        Baltimore Social-Environmental Collaborative
                    </Typography>
                    <div style={{marginLeft:'auto'}}>
                        <JoinButton project={project} size="medium" includeEdit includeMembers />
                    </div>
                </div>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        flexWrap: 'wrap',
                        rowGap: '2rem',
                        columnGap: '1rem',
                        marginTop: '-1.5rem',
                    }}
                >
                    <div
                        style={{
                            flex: '1',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.25rem',
                            minWidth: '24rem',
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '1rem',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'flex-end',
                                    gap: '1rem',
                                    marginTop: '0.5rem',
                                }}
                            >
                                <Typography variant='h5'>
                                    About
                                </Typography>
                            </div>
                            <Typography variant='body1'>
                                {project.description}
                                <br/><br/>
                                MSD-LIVE Contact: <Link
                                    href={`mailto:${project.contact.email}`}
                                >{project.contact.firstName} {project.contact.lastName}</Link>
                            </Typography>
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.5rem',
                                minWidth: '24rem',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'flex-end',
                                    gap: '1rem',
                                    marginTop: '1rem',
                                }}
                            >
                                <Typography variant='h6'>
                                    Additional Resources
                                </Typography>
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '0.25rem',
                                }}
                            >
                                {bsecSettings.informationalLinks.map((l, i) => (
                                    <AdditionalResource
                                        key={i}
                                        {...l}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                    <Flexy>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '1rem',
                                minWidth: '24rem',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'flex-end',
                                    gap: '1rem',
                                    marginTop: '0.5rem',
                                }}
                            >
                                <Typography variant='h5'>
                                    Highlights
                                </Typography>
                            </div>
                            <div
                                style={{
                                    paddingInline: '1.5rem',
                                    paddingTop: '1rem',
                                    paddingBottom: '1.25rem',
                                    borderRadius: '0.5rem',
                                    boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
                                    backgroundColor: '#f3f4f6',
                                }}
                            >
                                <Statistics
                                    project={bsecSettings.msdliveProjectStatsKey}
                                />
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'flex-end',
                                        gap: '1rem',
                                        marginTop: '1.25rem',
                                        marginBottom: '1rem',
                                    }}
                                >
                                    <Typography variant='h6'>
                                        Featured Datasets
                                    </Typography>
                                </div>
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '1rem',
                                    }}
                                >
                                    {bsecSettings.featuredResources.map((r, i) => (
                                        <FeaturedResource
                                            key={i}
                                            {...r}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </Flexy>
                </div>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.5rem',
                        flex: '1',
                        flexShrink: '0',
                        minWidth: '24rem',
                        marginTop: '1rem',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'flex-end',
                            gap: '1rem',
                            marginTop: '0.5rem',
                        }}
                    >
                        <Typography variant='h6'>
                            Data Products
                        </Typography>
                    </div>
                    <DataProducts projectSearchKey={bsecSettings.msdliveProjectSearchKey} />
                </div>
            </div>
        );
    }

    return (
        <Root className={className} maxWidth={theme.maxToolWidth}>
            {body}
        </Root>
      );

}

export default observer(BSECLanding);
